import React from 'react';
import {
  alpha, Box, ButtonBase, Stack, Theme, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import pingwinMark from '../../assets/logos/pingwin-mark.png';
import focusRing from '../../theme/focus';

const SMALL_MARK_SIZE = 32;

// The theme gives body1's font size as a bare number of pixels and its line height as a
// factor. In a CSS calc() their product has no unit and cannot be taken from 32px, so the
// browser would drop the margin. The first line's height is worked out in px here instead.
const toPx = (value: string | number | undefined, remPx: number) => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value ?? '');
  return value?.trim().endsWith('rem') ? parsed * remPx : parsed;
};

const firstLineHeightPx = (theme: Theme) => {
  const { body1, htmlFontSize = 16 } = theme.typography;
  const fontSize = toPx(body1.fontSize, htmlFontSize);
  const { lineHeight } = body1;
  if (typeof lineHeight === 'string' && /(px|rem)\s*$/.test(lineHeight)) {
    return toPx(lineHeight, htmlFontSize);
  }
  return (parseFloat(String(lineHeight ?? 1.5)) || 1.5) * fontSize;
};

const INTRO = 'Ask about cases, rules and entities. Pingwin sees only what you have access to.';

const PinguStartingPrompts = ({
  prompts,
  onSelect,
  showMark = false,
}: {
  prompts: string[];
  onSelect: (prompt: string) => void;
  // Drawn when the large mark in the transcript doesn't fit, so the empty state keeps one mark.
  showMark?: boolean;
}) => (
  <Stack spacing={2} sx={{ px: 4, pb: 1 }}>
    {showMark ? (
      <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 1 }}>
        <Box
          component="img"
          src={pingwinMark}
          alt=""
          sx={(theme) => {
            // Centred on the intro's first line, and overhanging it rather than making
            // the row taller, so the transcript keeps the same height with either mark.
            const overhang = (firstLineHeightPx(theme) - SMALL_MARK_SIZE) / 2;
            return {
              width: SMALL_MARK_SIZE,
              height: SMALL_MARK_SIZE,
              flexShrink: 0,
              my: `${overhang}px`,
            };
          }}
        />
        <Typography variant="body1" color="text.secondary">
          {INTRO}
        </Typography>
      </Stack>
    ) : (
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {INTRO}
      </Typography>
    )}
    {prompts.map((prompt) => (
      <ButtonBase
        key={prompt}
        onClick={() => onSelect(prompt)}
        sx={{
          justifyContent: 'flex-start',
          gap: 2,
          width: '100%',
          px: 3,
          py: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          textAlign: 'left',
          color: 'text.primary',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.emphasisText.main, 0.08),
          },
          '&:focus-visible': { boxShadow: focusRing },
        }}
      >
        <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body1">{prompt}</Typography>
      </ButtonBase>
    ))}
  </Stack>
);

export default PinguStartingPrompts;
